import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 122 Backoffice Daily Face Check API
// (BackofficeFaceCheckController/FaceCheckService). Same base/auth
// convention as ../abnormal/abnormalApi.js/../fakeLocation/fakeLocationApi.js -
// apiClient.get/post with no `base` override, the same Bearer-session-
// authenticated backend every other backoffice controller uses. This file
// never calls an external face-recognition provider, never uploads/reads a
// raw face image, and never sends a real notification - every write here
// maps 1:1 to one of the four real P122 routes below.

const FACE_CHECK_PATH = "/backoffice/face-check/daily";

// Exactly the query param names BackofficeFaceCheckController.ListDaily
// declares, in the same order as the controller signature - anything else
// passed to listFaceCheckDaily is silently dropped rather than forwarded, so
// a typo'd key can never accidentally reach the backend as an unfiltered
// pass-through. Scope-isolated visibility always happens on the backend from
// the caller's own session, never from a value this file sends.
const DAILY_LIST_PARAM_KEYS = [
  "date",
  "complianceStatus",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "agentId",
  "employerId",
  "workerId",
  "search",
  "page",
  "pageSize",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listFaceCheckDaily(params = {}) {
  return apiClient.get(`${FACE_CHECK_PATH}${buildQuery(DAILY_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty faceCheckEventId - this file never
// issues a request for a missing/blank id (the caller only calls this once a
// row/card is actually clicked).
export function getFaceCheckDailyDetail(faceCheckEventId) {
  if (!faceCheckEventId) {
    throw new Error("getFaceCheckDailyDetail requires a faceCheckEventId");
  }
  return apiClient.get(`${FACE_CHECK_PATH}/${encodeURIComponent(faceCheckEventId)}`);
}

// decision must be one of FaceCheckReviewDecisions (Accepted/Rejected/
// NeedsCorrection) - the backend itself rejects anything else with a 400.
// reviewNote is only required by the backend when decision is Rejected or
// NeedsCorrection; this wrapper never enforces that rule itself (the page
// validates before calling), it only omits reviewNote entirely when blank
// rather than sending an empty string.
export function reviewFaceCheckDaily(faceCheckEventId, { decision, reviewNote } = {}) {
  if (!faceCheckEventId) {
    throw new Error("reviewFaceCheckDaily requires a faceCheckEventId");
  }
  const body = { decision: decision || null };
  if (reviewNote) {
    body.reviewNote = reviewNote;
  }
  return apiClient.post(`${FACE_CHECK_PATH}/${encodeURIComponent(faceCheckEventId)}/review`, body);
}

// SuperAdmin-only (FaceCheckService.ScanMissingDailyAsync). dryRun defaults
// true, matching FaceCheckMissingScanRequest.DryRun's own default
// (FaceCheckDtos.cs) - a caller must explicitly opt out of dryRun to ever
// create a real FaceCheckMissing abnormal.
export function runFaceCheckMissingScan({ thaiDate, dryRun = true } = {}) {
  return apiClient.post(`${FACE_CHECK_PATH}/missing-scan`, {
    thaiDate: thaiDate || null,
    dryRun: Boolean(dryRun),
  });
}
